import type { QualityDeal } from "@west-tennessee-consulting/crm-scoring/deal-quality";

import type { Deal } from "@/lib/types/database/deals";
import { unwrapArray } from "@/lib/utils/graphql/graphql-types";

/**
 * Project a `deals` row onto the subset the quality rules read.
 *
 * `QualityDeal` lives in `@west-tennessee-consulting/crm-scoring`, which has no
 * Supabase dependency and therefore no notion of pg_graphql's `Maybe<T>` — it
 * declares its optional fields `string | undefined`, not `string | null`. Hub's
 * `Deal` is a codegen projection (#2633), so a nullable column arrives as
 * `null`, and the two shapes no longer line up by accident.
 *
 * They used to, because `Deal` was hand-written and claimed columns the DB can
 * null were required. That is the whole bug this mapper replaces: the rules were
 * being handed values whose declared type said a null could not appear.
 *
 * `null` is collapsed to `undefined` rather than to a placeholder — a rule that
 * asks "is `close_date` missing?" must see missing, not `""` or `0`. The one
 * exception is `stage`, which the rules require: a stage-less deal is reported
 * as `lead`, matching `adaptGraphQLDealToDeal`'s long-standing default.
 */
export function toQualityDeal(deal: Deal): QualityDeal {
  return {
    id: deal.id,
    title: deal.title,
    company_name: deal.company_name ?? undefined,
    revenue: deal.revenue,
    stage: deal.stage ?? "lead",
    close_date: deal.close_date ?? undefined,
    close_probability: deal.close_probability ?? undefined,
    assigned_users: unwrapArray(deal.assigned_users),
    won_at: deal.won_at ?? undefined,
    lost_at: deal.lost_at ?? undefined,
    cost: deal.cost ?? undefined,
    schedule_status: deal.schedule_status ?? undefined,
    budget_status: deal.budget_status ?? undefined,
    scope_status: deal.scope_status ?? undefined,
    alignment_status: deal.alignment_status ?? undefined,
  };
}

/** {@link toQualityDeal} over an array. */
export function toQualityDeals(deals: Deal[]): QualityDeal[] {
  return deals.map(toQualityDeal);
}

/**
 * Project a suggested quality fix onto a `deals` update payload.
 *
 * The inverse of {@link toQualityDeal}, and narrower on purpose. `QualityDeal`
 * types `schedule_status` / `budget_status` / … as plain `string | null`, where
 * the column is the `project_health_status` enum — so a whole-object spread
 * would not type-check even though the fixes `getSuggestedFix` produces never
 * touch those fields. Naming the writable columns says which ones a
 * deterministic fix is allowed to change, and makes adding another one a
 * deliberate edit.
 *
 * While `Deal` was hand-written and loosened the same fields the package does,
 * this was an `as Deal & { id: string }` at each call site (#2633).
 */
export function toDealUpdate(
  id: string,
  fix: Partial<QualityDeal>
): Partial<Deal> & { id: string } {
  const update: Partial<Deal> & { id: string } = { id };
  if (fix.stage !== undefined) update.stage = fix.stage;
  if (fix.won_at !== undefined) update.won_at = fix.won_at;
  if (fix.lost_at !== undefined) update.lost_at = fix.lost_at;
  if (fix.close_date !== undefined) update.close_date = fix.close_date;
  if (fix.close_probability !== undefined) update.close_probability = fix.close_probability;
  if (fix.revenue !== undefined) update.revenue = fix.revenue;
  if (fix.cost !== undefined) update.cost = fix.cost;
  return update;
}
